import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, MapPin, Truck, Store, ShoppingCart, Minus, Plus } from "lucide-react";
import { FavoriteButton } from "@/components/FavoriteButton";
import { ShareButton, useTrackView } from "@/components/ShareButton";
import ReportButton from "@/components/ReportButton";
import { FeatureButton } from "@/components/featured/FeatureButton";
import { FeaturedBadge } from "@/components/featured/FeaturedBadge";
import { money, VerifiedBadge } from "@/components/marketplace/shared";
import { Rating, ReviewsList } from "@/components/marketplace/Reviews";
import { MessageButton } from "@/components/messages/shared";

export default function ProductDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  useTrackView("product", p ? p.id : null);
  const [qty, setQty] = useState(1);
  const [mode, setMode] = useState(null);
  const [variant, setVariant] = useState({});
  const [busy, setBusy] = useState(false);
  const [photo, setPhoto] = useState(0);

  useEffect(() => {
    api.get(`/products/${id}`).then(r => { setP(r.data); setMode(r.data.fulfilment?.modes?.[0] || null); }).catch(() => setP(false));
  }, [id]);

  if (p === null) return <div className="page pt-8 text-center text-zinc-500">A carregar…</div>;
  if (p === false) return <div className="page pt-8 text-center text-zinc-500">Produto não encontrado.</div>;

  const add = async () => {
    if (!user) return nav("/login");
    setBusy(true);
    try {
      const v = Object.keys(variant).length ? variant : null;
      await api.post("/cart/items", { product_id: p.id, qty, variant: v, fulfilment_mode: mode });
      toast.success("Adicionado ao carrinho", { action: { label: "Ver carrinho", onClick: () => nav("/marketplace/carrinho") } });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const f = p.fulfilment || { modes: [], delivery: {}, pickup: {} };
  return (
    <div className="page fade-up">
      <Link to="/marketplace" data-testid="product-back" className="inline-flex items-center gap-1 text-sm text-zinc-500 py-3"><ArrowLeft size={14} /> Marketplace</Link>
      <div className="rounded-2xl overflow-hidden bg-white border border-zinc-200">
        <div className="aspect-square bg-zinc-100 relative">
          {p.photos?.[photo] && <img src={p.photos[photo]} alt={p.name} className="w-full h-full object-cover" />}
          {p.status === "sold_out" && <span className="absolute top-3 left-3 bg-orange-600 text-white text-xs font-bold px-2 py-1 rounded">Esgotado</span>}
        </div>
        {p.photos?.length > 1 && (
          <div className="flex gap-2 p-3 overflow-x-auto">
            {p.photos.map((ph, i) => <img key={i} src={ph} alt="" onClick={() => setPhoto(i)} className={`w-14 h-14 rounded-lg object-cover border-2 ${i === photo ? "border-black" : "border-transparent"}`} />)}
          </div>
        )}
        <div className="p-5">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">{p.category}</div>
          <h1 data-testid="product-name" className="font-display text-2xl font-extrabold leading-tight mb-1 flex items-center gap-2 flex-wrap">{p.name}{p.featured_paid && p.featured_until && new Date(p.featured_until) > new Date() && <FeaturedBadge testid="product-featured-badge" />}</h1>
          <div data-testid="product-price" className="font-display text-3xl font-extrabold text-[#c8102e] mb-1 flex items-center justify-between gap-2"><span>{money(p.price_minor, p.currency)}</span><span className="flex items-center gap-1"><ShareButton type="product" id={p.id} testid="product-share-btn" /><FavoriteButton type="product" id={p.id} count={p.favorites_count} size="lg" testid="product-fav-btn" /></span></div>
          <div className="mb-3"><Rating avg={p.rating_avg} count={p.rating_count} size="lg" testid="product-rating" /></div>

          <Link to={`/negocios/${p.seller_id}`} data-testid="product-seller-link"
                className="flex items-center gap-3 p-3 rounded-xl bg-zinc-50 border border-zinc-200 mb-4 hover:border-black">
            <div className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center overflow-hidden">
              {p.seller?.logo_url ? <img src={p.seller.logo_url} alt="" className="w-full h-full object-cover" /> : <Store size={16} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate flex items-center gap-1">{p.seller?.shop_name || p.seller_name}<VerifiedBadge verified={p.seller_verified} /></div>
              <div className="text-[11px] text-zinc-500 flex items-center gap-2"><span className="flex items-center gap-1"><MapPin size={10} /> {p.city}</span><Rating avg={p.seller?.rating_avg} count={p.seller?.rating_count} testid="product-seller-rating" /></div>
            </div>
          </Link>

          <p className="text-sm text-zinc-700 leading-relaxed whitespace-pre-line mb-4">{p.description}</p>

          {p.variants?.map(v => (
            <div key={v.name} className="mb-3">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1">{v.name}</div>
              <div className="flex flex-wrap gap-1.5">
                {v.options.map(o => <button key={o} onClick={() => setVariant(x => ({ ...x, [v.name]: o }))} className="chip !text-[11px]" data-active={variant[v.name] === o}>{o}</button>)}
              </div>
            </div>
          ))}

          <div className="mb-3">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1">Entrega / Levantamento</div>
            <div className="space-y-2">
              {f.modes.includes("delivery") && (
                <button data-testid="mode-delivery" onClick={() => setMode("delivery")} className={`w-full text-left p-3 rounded-xl border text-xs ${mode === "delivery" ? "border-black bg-zinc-50" : "border-zinc-200"}`}>
                  <div className="font-semibold flex items-center gap-1"><Truck size={12} /> Entrega — {f.delivery.price_minor ? money(f.delivery.price_minor, p.currency) : "grátis"}</div>
                  {f.delivery.area && <div className="text-zinc-500 mt-0.5">Área: {f.delivery.area}</div>}
                  {f.delivery.conditions && <div className="text-zinc-500">{f.delivery.conditions}</div>}
                </button>
              )}
              {f.modes.includes("pickup") && (
                <button data-testid="mode-pickup" onClick={() => setMode("pickup")} className={`w-full text-left p-3 rounded-xl border text-xs ${mode === "pickup" ? "border-black bg-zinc-50" : "border-zinc-200"}`}>
                  <div className="font-semibold flex items-center gap-1"><MapPin size={12} /> Levantamento{f.pickup.location ? ` — ${f.pickup.location}` : ""}</div>
                  {f.pickup.hours && <div className="text-zinc-500 mt-0.5">Horário: {f.pickup.hours}</div>}
                  {f.pickup.instructions && <div className="text-zinc-500">{f.pickup.instructions}</div>}
                </button>
              )}
            </div>
          </div>

          {p.status === "published" && (
            <div className="flex items-center gap-2 mb-2">
              <div className="flex items-center border border-zinc-200 rounded-xl">
                <button data-testid="qty-minus" onClick={() => setQty(q => Math.max(1, q - 1))} className="p-2.5"><Minus size={14} /></button>
                <span data-testid="qty-value" className="w-8 text-center text-sm font-bold">{qty}</span>
                <button data-testid="qty-plus" onClick={() => setQty(q => Math.min(p.stock, q + 1))} className="p-2.5"><Plus size={14} /></button>
              </div>
              <button data-testid="add-to-cart" onClick={add} disabled={busy || p.stock < 1}
                      className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-[#c8102e] hover:bg-[#a50c24] disabled:opacity-50 text-white font-semibold text-sm transition-colors">
                <ShoppingCart size={16} /> Adicionar ao carrinho
              </button>
            </div>
          )}
          <div className="text-[11px] text-zinc-500 mb-3">{p.stock > 0 ? `${p.stock} em stock` : "Sem stock"}{p.sku ? ` · Ref. ${p.sku}` : ""}</div>
          <div className="mb-3"><FeatureButton targetType="product" targetId={p.id} targetName={p.name} testid="product-feature-btn" /></div>
          <div className="pt-3 border-t border-zinc-100 flex justify-between items-center"><MessageButton context_type="product" context_id={p.id} label="Mensagem à loja" /><ReportButton target_type="product" target_id={p.id} /></div>
        </div>
      </div>
      <ReviewsList target_type="product" target_id={p.id} title="Avaliações deste produto" testid="product-reviews" />
      <div className="h-8" />
    </div>
  );
}
